import { useT } from '@kmgestao/i18n';
import {
  Badge,
  PageContainer,
  PageIntroCard,
  PageSectionCard,
  Skeleton,
  toast,
} from '@kmgestao/ui';

import { useCurrentClinicId } from '../../../features/auth/index.js';
import {
  PALETTE_KEYS,
  useClinicTheme,
  useUpdateClinicPalette,
  type PaletteKey,
} from '../../../features/clinic-appearance/index.js';
import { ClinicShell } from '../ClinicShell.js';

import { ClinicLogoUploader } from './ClinicLogoUploader.js';
import { PaletteCard } from './PaletteCard.js';

/**
 * Página `/identidade-visual` — owner escolhe paleta de cores da
 * plataforma (PR-M2-04).
 *
 * Acesso: `clinic_owner` / `clinic_admin` via `<RequireClinicAdmin>`.
 * Dentro do gate de pagamento (operação só com plataforma liberada).
 *
 * Conteúdo:
 *   - **Card 1: Paleta de cores** — 4 cards visuais lado-a-lado.
 *     Owner clica "Aplicar" → mutation atualiza `clinics.theme_palette`
 *     → cache invalida → `PaletteThemeProvider` re-aplica CSS vars
 *     → toda UI da plataforma muda visualmente sem reload.
 *   - **Card 2: Logo da clínica** — placeholder informativo. Upload
 *     real (com Storage + image processing) entra em PR específica
 *     (M2-04b ou M2-06).
 *
 * Por que 4 paletas curadas em vez de hex livre: garantir contraste
 * WCAG AA + harmonia visual + ausência de cores agressivas.
 * Documentado em `docs/clinic/appearance.md`.
 */
export function ClinicAppearancePage() {
  const { t } = useT('clinics');
  const clinicId = useCurrentClinicId();
  const themeQ = useClinicTheme(clinicId);
  const updateMutation = useUpdateClinicPalette();

  const handleSelect = async (key: PaletteKey) => {
    if (!clinicId) return;
    try {
      await updateMutation.mutateAsync({ clinicId, palette: key });
      toast.success(t('appearance.palette.feedback.successTitle'), {
        description: t('appearance.palette.feedback.successDescription'),
      });
    } catch (err) {
      toast.error(t('appearance.palette.feedback.errorTitle'), {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  const currentPalette = themeQ.data?.theme_palette ?? 'default';

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('appearance.eyebrow')}
          title={t('appearance.title')}
          description={t('appearance.description')}
          meta={<Badge variant="outline">{t('appearance.metaBadge')}</Badge>}
        />

        {!clinicId ? (
          <PageSectionCard
            title={t('appearance.noClinic.title')}
            description={t('appearance.noClinic.description')}
          >
            <div />
          </PageSectionCard>
        ) : (
          <>
            <PageSectionCard
              title={t('appearance.palette.title')}
              description={t('appearance.palette.description')}
              contentClassName="space-y-4"
            >
              {themeQ.isLoading ? (
                <PaletteGridSkeleton />
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {PALETTE_KEYS.map((key) => (
                    <PaletteCard
                      key={key}
                      paletteKey={key}
                      isCurrent={currentPalette === key}
                      isSubmitting={updateMutation.isPending}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </PageSectionCard>

            <PageSectionCard
              title={t('appearance.logo.title')}
              description={t('appearance.logo.description')}
              contentClassName="space-y-3"
            >
              <ClinicLogoUploader
                clinicId={clinicId}
                currentLogoUrl={themeQ.data?.logo_url ?? null}
              />
            </PageSectionCard>
          </>
        )}
      </PageContainer>
    </ClinicShell>
  );
}

function PaletteGridSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-3 rounded-xl border border-border bg-card p-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ))}
    </div>
  );
}
